import React, { useEffect, useRef, useState } from 'react'
import { IconChevronLeft, IconChevronRight, IconMenu2 } from '@tabler/icons-react'
import { useContainerQuery } from 'react-container-query'
import { ErrorBoundary } from 'react-error-boundary'
import { Outlet, useLocation } from 'react-router-dom'
import { useScrollLock } from 'usehooks-ts'
import {
  Box,
  AppShell as MantineAppShell,
  Stack,
  useMantineTheme,
  VisuallyHidden,
} from '@mantine/core'
import { useDisclosure, useFocusTrap, useMediaQuery } from '@mantine/hooks'
import { containerQueries } from './container-queries'
import { Logo } from './logo/logo'
import { MainMenu, TopLevelLinkProps } from './main-menu/main-menu'
import { MenuError } from './menu-error/menu-error'
import { MobileHeader } from './mobile-header/mobile-header'
import { OutagePoll } from './outage-poll/outage-poll'
import { UserMenu } from './user-menu/user-menu'
import { ErrorPage } from '../error-page/error-page'
import classes from './app-shell.module.css'
import clsx from 'clsx'

export interface AppShellProps {
  menuLinks: Array<TopLevelLinkProps>
  searchComponent?: React.ReactNode
  logoComponent?: React.ReactNode
  logoutComponent?: React.ReactNode
}

export interface OutletContextProps {
  closeDesktopMenu: VoidFunction
  isWideContainer: boolean
  setSidebarComponent: any
}

export const AppShell = ({
  menuLinks,
  searchComponent,
  logoComponent,
  logoutComponent,
}: AppShellProps) => {
  const theme = useMantineTheme()
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints?.md})`)

  // Close the sidebar if there is a query param to close the sidebar in desktop mode: ?closeSidebar=true
  const { search } = useLocation()

  useEffect(() => {
    const searchParams = new URLSearchParams(search)
    if (isDesktop === true && searchParams.get('closeSidebar') === 'true') {
      closeDesktopMenu()
    }
  }, [search, isDesktop])

  // Allow pages to set sidebar content dynamically
  const [sidebarComponent, setSidebarComponent] = useState<React.ReactNode | null>(null)
  const updateSidebarComponent = (component: React.ReactNode | null) => {
    setSidebarComponent(component)
    if (component === null) closeSidebar()
  }

  // Used to lock scroll on body element when mobile menu open
  // Note: bug in development mode where scroll lock doesn't work as expected
  // See: https://github.com/juliencrn/usehooks-ts/issues/646
  // Tested in deployed environment and works as expected (5/5/25).
  const { lock, unlock } = useScrollLock({
    autoLock: false,
  })

  // Manage sidebar/content container state to allow for toggling sidebar open/closed animation
  const [sidebarOpened, { toggle: toggleSidebar, open: openSidebar, close: closeSidebar }] =
    useDisclosure(false, {
      onOpen: () => {
        if (!isDesktop) {
          lock()
        }
      },
      onClose: () => {
        unlock()
      },
    })
  const sidebarClasses = clsx(classes.sidebar, {
    [classes.sidebarOpen]: sidebarOpened && sidebarComponent,
  })
  const mainAppContainerInnerClasses = clsx(classes.mainAppContainerInner, {
    [classes.sidebarIsOpen]: sidebarOpened && sidebarComponent,
  })

  // Make sure scroll restored if viewport resized after mobile menu opened
  useEffect(() => {
    if (isDesktop) {
      unlock()
    }
  }, [isDesktop])

  /*
   * References to open/close buttons on mobile so we can shift
   * keyboard focus appropriately on open/close.
   */
  const mobileCloseBtn = useRef<HTMLButtonElement>(null)
  const mobileOpenBtn = useRef<HTMLButtonElement>(null)
  const triggerFocusOnCloseBtn = () => mobileCloseBtn.current?.focus()
  const triggerFocusOnOpenBtn = () => mobileOpenBtn.current?.focus()

  const [mobileOpened, { toggle: toggleMobile, close: closeMobileMenu }] = useDisclosure(false, {
    onOpen: () => {
      /**
       * Timeout allows hidden attribute to be removed from menu
       * panel contents first (otherwise focus not possible)
       */
      setTimeout(triggerFocusOnCloseBtn, 100)
      if (!isDesktop) {
        lock()
      }
    },
    onClose: () => {
      triggerFocusOnOpenBtn()
      unlock()
    },
  })
  const [desktopOpened, { toggle: toggleDesktop, close: closeDesktopMenu }] = useDisclosure(true)

  // Used to prevent keyboard focus leaving mobile menu panel when open
  const focusTrapRef = useFocusTrap(true)

  const [params, containerRef] = useContainerQuery(containerQueries, {})

  return (
    <div className={classes.appShell}>
      <OutagePoll />
      <a href='#a11y-main-content' className={classes.skipLink}>
        Skip to content
      </a>
      {!isDesktop && (
        <MobileHeader
          onClickMenuToggle={() => toggleMobile()}
          logoComponent={logoComponent}
          ref={mobileOpenBtn}
        />
      )}
      <MantineAppShell
        navbar={{
          width: { md: 300 },
          breakpoint: 'md',
          collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
        }}
        padding={0}
        classNames={{
          navbar: classes.navBar,
          main: classes.mainAppContainer,
        }}
        withBorder={false}
      >
        <MantineAppShell.Navbar aria-label='Main'>
          <ErrorBoundary fallbackRender={MenuError}>
            {/*
             * Apply hidden attribute to prevent keyboard access/exposing
             * elements to a11y tree when panel visually hidden
             */}
            <div
              className={classes.menuContainer}
              data-testid='menu-container'
              hidden={(isDesktop && !desktopOpened) || (!isDesktop && !mobileOpened)}
              // Create focus trap within mobile menu panel when open:
              ref={!isDesktop && mobileOpened ? focusTrapRef : null}
            >
              {!isDesktop && (
                <MobileHeader
                  onClickMenuToggle={() => toggleMobile()}
                  closeMobileMenu={closeMobileMenu}
                  logoComponent={logoComponent}
                  menuIcon='cross'
                  ref={mobileCloseBtn}
                />
              )}
              <Stack gap='xl' align='start' className={classes.navBarContent}>
                {isDesktop && (
                  <Logo logoComponent={logoComponent} closeMobileMenu={closeMobileMenu} />
                )}
                {searchComponent && (
                  // Use a div here to prevent the search input from pushing the menu items down
                  <Box w='100%'>{searchComponent}</Box>
                )}
                <MainMenu links={menuLinks} closeMobileMenu={closeMobileMenu} />
                <UserMenu closeMobileMenu={closeMobileMenu} logoutComponent={logoutComponent} />
              </Stack>
            </div>
            {isDesktop && (
              <button
                type='button'
                className={classes.toggleDesktopMenuBtn}
                onClick={toggleDesktop}
                data-testid='toggle-desktop-menu-btn'
              >
                {desktopOpened ? (
                  <IconChevronLeft data-testid='desktop-close-menu' />
                ) : (
                  <IconMenu2 data-testid='desktop-open-menu' />
                )}
              </button>
            )}
          </ErrorBoundary>
        </MantineAppShell.Navbar>
        <MantineAppShell.Main id='a11y-main-content'>
          {sidebarComponent && (
            <div className={sidebarClasses} onFocus={openSidebar}>
              <button type='button' className={classes.toggleSidebarBtn} onClick={toggleSidebar}>
                <VisuallyHidden>{sidebarOpened ? 'Hide' : 'Show'} sidebar</VisuallyHidden>
                {sidebarOpened ? <IconChevronRight /> : <IconChevronLeft />}
              </button>
              {sidebarOpened && <div className={classes.sidebarContent}>{sidebarComponent}</div>}
            </div>
          )}
          <div ref={containerRef} className={mainAppContainerInnerClasses}>
            <ErrorBoundary fallbackRender={ErrorPage}>
              <Outlet
                context={{
                  closeDesktopMenu,
                  isWideContainer: params.isWideContainer,
                  setSidebarComponent: updateSidebarComponent,
                }}
              />
            </ErrorBoundary>
          </div>
        </MantineAppShell.Main>
      </MantineAppShell>
    </div>
  )
}
